import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AdapterProps, SelectAdapterProps } from '../types';
import Autocomplete from '../../Autocomplete/Autocomplete';
import InputLabel from '../../InputLabel/InputLabel';
import css from '../filters.module.scss';
import Button from 'components/v2/Button/Button';
import Icon from '../../Icon/Icon';
import { useOptionalReportContext } from '../../../../modules/reports/context/ReportContext';
import useSelectAdapterOptionsQuery from '../../../../modules/reports/hooks/use-select-adapter-options-query';
import { useTranslation } from 'utils/translation';
import { ClickAwayListener } from '@mui/material';
import debounce from 'lodash/debounce';

type Value = {
  id: string;
  label: string;
};

type AutocompleteProps = React.ComponentProps<typeof Autocomplete>;

type Variant = 'default' | 'filter';

const toSelectedIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }

  if (value == null || value === '') {
    return [];
  }

  return [String(value)];
};

export const SelectAdapter = (
  props: AdapterProps<Value> &
    SelectAdapterProps & {
      hasPagination?: boolean;
      queryParams?: {
        id: string;
      };
      variant?: Variant;
    },
) => {
  const {
    name,
    label,
    onChange,
    onRemove,
    settings,
    value,
    queryParams,
    hasPagination = false,
    variant = 'filter',
    emptyPlaceholder,
  } = props;
  const { optionsUrl = null, dependencies = [] } = settings;
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pendingEmptySearchRefetch = useRef(false);

  const query = useSelectAdapterOptionsQuery({
    ...queryParams,
    name,
    searchQuery,
    url: optionsUrl,
    dependencies,
    hasPagination,
  });

  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  const debouncedRefetch = useMemo(
    () =>
      debounce(() => {
        void refetchRef.current();
      }, 250),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedRefetch.cancel();
    };
  }, [debouncedRefetch]);

  useLayoutEffect(() => {
    if (!pendingEmptySearchRefetch.current || searchQuery !== '') {
      return;
    }

    pendingEmptySearchRefetch.current = false;
    void refetchRef.current();
  }, [searchQuery]);

  const selectedIds = useMemo(() => toSelectedIds(value), [value]);
  const selectedOptions = useMemo(() => {
    const data = query.data || [];

    return selectedIds.map((id) => {
      const found = data.find((item) => item.id === id);

      return found ?? { id, label: id };
    });
  }, [selectedIds, query.data]);

  const options = useMemo(() => {
    const data = query.data || [];
    const dataIds = new Set(data.map((item) => item.id));
    const missing = selectedOptions.filter((item) => !dataIds.has(item.id));

    return missing.length > 0 ? [...missing, ...data] : data;
  }, [query.data, selectedOptions]);

  const onInputChange: AutocompleteProps['onInputChange'] = (
    event,
    nextValue,
    reason,
  ) => {
    // Options refreshing replaces `value` and MUI emits `reset` with an empty
    // string. Treating that as user input clears the query and refetches the
    // full list.
    if (reason === 'reset') {
      return;
    }

    pendingEmptySearchRefetch.current = false;
    setSearchQuery(nextValue);
    debouncedRefetch();
  };

  const resetInput = useCallback(() => {
    debouncedRefetch.cancel();

    setSearchQuery((prev) => {
      if (prev === '') {
        return prev;
      }

      pendingEmptySearchRefetch.current = true;

      return '';
    });
  }, [debouncedRefetch]);

  const variants = {
    default: {
      wrapper: '',
      label: '',
      modifier: undefined,
      canRemove: false,
    },
    filter: {
      wrapper: css.filter,
      label: css.label,
      modifier: 'filter',
      canRemove: true,
    },
  } as const;

  const theme = variants[variant];

  return (
    <ClickAwayListener onClickAway={resetInput}>
      <div className={theme.wrapper} onBlur={resetInput}>
        {label && <InputLabel className={theme.label}>{label}</InputLabel>}
        <Autocomplete
          border={false}
          modifier={theme.modifier}
          clearText={t('filters.clear_filter')}
          multiple
          options={options}
          value={selectedOptions}
          onChange={(event, nextValue) => {
            onChange!(nextValue.map((i) => i.id));
          }}
          getOptionKey={(option) => option.id}
          isOptionEqualToValue={(option, optionValue) =>
            option.id === optionValue.id
          }
          onInputChange={onInputChange}
          inputValue={searchQuery}
          emptyPlaceholder={emptyPlaceholder}
        />
        {theme.canRemove && (
          <Button
            tabIndex={-1}
            onClick={onRemove ? onRemove : () => onChange?.([])}
            variant="text"
            startIcon={<Icon name="fa fa-times" />}
            className={css['remove-button']}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};

export default (props: AdapterProps<Value> & SelectAdapterProps) => {
  const report = useOptionalReportContext();
  const { id = undefined } = report || {};

  return (
    <SelectAdapter
      {...props}
      queryParams={
        id
          ? {
              id,
            }
          : undefined
      }
    />
  );
};
